/* ════════════════════════════════════════════════════
 *  <kik-peek-advance> — hovering the clipped trailing slide advances the track.
 *
 *  A carousel that bleeds off the trailing edge always shows a partially
 *  visible "peek" slide. Hovering that slide reads as "I want to see this one",
 *  so the track slides forward to reveal it and the products behind it.
 *
 *  Wraps a <kik-swiper>; the shared component is left untouched. Put the class
 *  list on this element and give it `display: block` — custom elements are
 *  inline by default.
 *
 *    <kik-peek-advance class="my-section__container">
 *      <kik-swiper>…</kik-swiper>
 *    </kik-peek-advance>
 *
 *  Deliberately inert when: the pointer isn't a mouse (touch has no hover, and
 *  a tap would fire this on the way to opening a product), the user prefers
 *  reduced motion, the track has nothing to scroll, or it's already at the end.
 *  The arrows remain the primary control in every one of those cases.
 *
 *  The keyboard counterpart — focusing a clipped card brings it into view —
 *  lives in component-kik-swiper.js, since it applies to every slider, not
 *  just ones with this hover behaviour attached.
 * ════════════════════════════════════════════════════ */

if (!customElements.get('kik-peek-advance')) {
  /* Hover intent — a cursor crossing the peek slide on its way somewhere else
     shouldn't move the track. Only a deliberate pause does. */
  const DWELL_MS = 260;

  /* Breathing room after a slide settles, so a cursor parked exactly on the
     edge advances at a readable pace instead of flying to the end. */
  const COOLDOWN_MS = 200;

  class KikPeekAdvance extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.swiper');

      this.addEventListener('pointerover', this.#onPointerOver);
      this.addEventListener('pointerleave', this.#onPointerLeave);
    }

    disconnectedCallback() {
      this.removeEventListener('pointerover', this.#onPointerOver);
      this.removeEventListener('pointerleave', this.#onPointerLeave);
      clearTimeout(this.dwellTimer);
      clearTimeout(this.coolTimer);
    }

    /* pointerover (not pointerenter) so it bubbles from the slide's children.

       `pointerType` is the whole mouse test, and deliberately the only one: it
       reports what fired THIS event. A `(pointer: fine)` check on top of it used
       to veto real mouse events on an iPad, which reports its primary pointer as
       coarse however many mice are attached (WebKit bug 209292). */
    #onPointerOver = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const slide = event.target.closest?.('.swiper-slide');
      if (!slide || !this.contains(slide)) return;

      /* The event refires for every descendant of the same slide — only react
         when the cursor actually crosses into a different one. */
      if (slide === this.hovered) return;
      this.hovered = slide;

      clearTimeout(this.dwellTimer);
      if (this.cooling || !this.#isTrailingPeek(slide)) return;

      this.dwellTimer = setTimeout(() => this.#advance(), DWELL_MS);
    };

    #onPointerLeave = () => {
      clearTimeout(this.dwellTimer);
      this.hovered = null;
    };

    /* True when the slide is cut off by the track's trailing edge — i.e. it
       starts inside the viewport but runs past it. Uses logical-ish geometry
       via the container rect so it holds in RTL as well as LTR. */
    #isTrailingPeek(slide) {
      const track = this.querySelector('.swiper');
      if (!track) return false;

      const t = track.getBoundingClientRect();
      const s = slide.getBoundingClientRect();
      const rtl = getComputedStyle(track).direction === 'rtl';

      return rtl
        ? s.left < t.left - 1 && s.right > t.left
        : s.right > t.right + 1 && s.left < t.right;
    }

    #advance() {
      const swiper = this.querySelector('kik-swiper')?.swiper;
      if (!swiper || swiper.isLocked || swiper.isEnd) return;

      this.cooling = true;
      swiper.slideNext();

      this.coolTimer = setTimeout(() => {
        this.cooling = false;
        /* Forget the last slide so a cursor still parked at the edge can
           re-arm once the next peek has settled underneath it. */
        this.hovered = null;
      }, (swiper.params.speed || 300) + COOLDOWN_MS);
    }
  }

  customElements.define('kik-peek-advance', KikPeekAdvance);
}
